import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BrandS, BrandXXS } from '#src/atoms/typography/index.tsx';
import { EdgeRectangleShape } from '#src/atoms/ui/EdgeRectangleShape/index.tsx';

import { RewardConfigImage } from './RewardConfigImage/index.tsx';
import { RewardConfigText } from './RewardConfigText/index.tsx';
import type { AnyRewardConfig_anyRewardConfigInterface } from './__generated__/index.graphql.ts';
import { getAnyRewardConfigColors } from './utils.ts';

export enum Variant {
  TEXT = 'TEXT',
  INLINE = 'INLINE',
  CHIP = 'CHIP',
  INLINE_CHIP = 'INLINE_CHIP',
}

const StyledHorizontal = styled(Horizontal)`
  padding: 1px 2px;
`;
const StyledVertical = styled(Vertical).attrs({ center: true, gap: 0 })`
  & > *:nth-child(2) {
    position: relative;
    margin-top: calc(-1 * var(--unit));
  }
`;

type Props = {
  anyRewardConfig: AnyRewardConfig_anyRewardConfigInterface;
  rarity?: Rarity | 'mix';
  variant?: Variant;
  customText?:
    | ReactNode
    | (({ rewardText }: { rewardText: ReactNode }) => ReactNode);
  hideText?: boolean;
  textVariantSize?: 'md' | 'lg';
  numberNotation?: 'standard' | 'compact';
};
export const AnyRewardConfig = ({
  anyRewardConfig,
  variant = Variant.CHIP,
  customText,
  rarity,
  hideText = false,
  textVariantSize = 'md',
  numberNotation = 'standard',
}: Props) => {
  const text =
    typeof customText === 'function'
      ? customText({
          rewardText: <RewardConfigText anyRewardConfig={anyRewardConfig} />,
        })
      : customText || (
          <RewardConfigText
            anyRewardConfig={anyRewardConfig}
            numberNotation={numberNotation}
          />
        );

  if (variant === Variant.TEXT) {
    if (textVariantSize === 'lg') {
      return (
        <Horizontal gap={0.5}>
          <RewardConfigImage
            anyRewardConfig={anyRewardConfig}
            rarity={rarity}
            size={4}
          />
          {!hideText && <BrandS color="var(--c-white)">{text}</BrandS>}
        </Horizontal>
      );
    }
    return (
      <Horizontal gap={0.5}>
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          rarity={rarity}
          size={3}
        />
        {!hideText && <BrandXXS color="var(--c-white)">{text}</BrandXXS>}
      </Horizontal>
    );
  }

  const { bg, border } = getAnyRewardConfigColors({
    anyRewardConfig,
    rarity,
  });

  if (variant === Variant.INLINE) {
    return (
      <EdgeRectangleShape
        border={border}
        background={bg}
        padding="2px var(--unit)"
        offset="var(--intermediate-unit)"
      >
        <StyledHorizontal gap={0.5}>
          <RewardConfigImage
            anyRewardConfig={anyRewardConfig}
            rarity={rarity}
            size={3}
          />
          {!hideText && <BrandXXS color="var(--c-white)">{text}</BrandXXS>}
        </StyledHorizontal>
      </EdgeRectangleShape>
    );
  }
  if (variant === Variant.INLINE_CHIP) {
    return (
      <EdgeRectangleShape
        border={border}
        background={bg}
        padding="2px var(--unit)"
        offset="var(--unit)"
      >
        <StyledHorizontal gap={0.5}>
          <RewardConfigImage
            anyRewardConfig={anyRewardConfig}
            rarity={rarity}
            size={3}
          />
          {!hideText && <BrandXXS color="var(--c-white)">{text}</BrandXXS>}
        </StyledHorizontal>
      </EdgeRectangleShape>
    );
  }

  return (
    <StyledVertical>
      <EdgeRectangleShape
        border={border}
        background={bg}
        padding="7px var(--intermediate-unit)"
        offset="12px"
      >
        <RewardConfigImage
          anyRewardConfig={anyRewardConfig}
          rarity={rarity}
          size={3}
        />
      </EdgeRectangleShape>
      {!hideText && <BrandS>{text}</BrandS>}
    </StyledVertical>
  );
};

AnyRewardConfig.fragments = {
  anyRewardConfigInterface: gql`
    fragment AnyRewardConfig_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
      ...getAnyRewardConfigColors_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${getAnyRewardConfigColors.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<AnyRewardConfig_anyRewardConfigInterface>,
};
